import React from "react";
import { FiLock, FiShield, FiDatabase, FiUsers, FiSettings, FiAlertCircle, FiClock, FiMail, FiRefreshCw } from "react-icons/fi";

function SemperorPrivacy() {
    const effectiveDate = "January 4, 2026";

    return (
        <div className="pt-24 pb-16 min-h-screen bg-yellow-600/50 relative overflow-hidden text-yellow-100">
            
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <div className="mb-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-yellow-500/20 border border-yellow-400/30 mb-6 shadow-lg shadow-yellow-500/20">
                        <FiShield className="text-4xl text-yellow-100" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 text-black tracking-tight">
                        Semperor Privacy Policy
                    </h1>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-400/20 rounded-full backdrop-blur-sm">
                        <FiClock className="text-yellow-300" />
                        <p className="text-black text-sm font-medium">Effective date: {effectiveDate}</p>
                    </div>
                </div>

                {/* Content Container */}
                <div className="space-y-8">
                    
                    {/* 1. Introduction */}
                    <Section number="1" title="Introduction: Your Privacy Matters" icon={<FiShield />}>
                        <p className="text-black leading-relaxed">
                            Welcome to Semperor. We are committed to being transparent about how we collect, use, and protect your information when you use our games and services.
                        </p>
                    </Section>

                    {/* 2. Information We Collect */}
                    <Section number="2" title="Information We Collect" icon={<FiDatabase />}>
                        <p className="mb-6 text-black">We collect information in three ways to provide and improve our services:</p>
                        
                        <div className="space-y-4">
                            <SubSection title="A. Information You Give Us Directly">
                                <ul className="space-y-3">
                                    <ListItem title="Contact/Identity Data">
                                        Your name, username, email address, or phone number (if you choose to provide it).
                                    </ListItem>
                                    <ListItem title="Account Credentials">
                                        Information necessary for your account, including your user ID and password.
                                    </ListItem>
                                    <ListItem title="Communication Data">
                                        Information you provide when contacting our support team or participating in our activities.
                                    </ListItem>
                                </ul>
                            </SubSection>

                            <SubSection title="B. Information We Collect Automatically">
                                <ul className="space-y-3">
                                    <ListItem title="Device & Connection Data">
                                        Your Device ID and IP address.
                                    </ListItem>
                                    <ListItem title="Gameplay Analytics">
                                        Data about how you use our games, such as time spent playing, levels achieved, and scores.
                                    </ListItem>
                                </ul>
                            </SubSection>

                            <SubSection title="C. Information from Third-Party Services">
                                <p className="text-black text-sm sm:text-base">
                                    If you choose to log in using platforms like Google Play Games, Apple Game Center, or similar services, we may receive limited account/profile information based on your privacy settings on those services.
                                </p>
                            </SubSection>
                        </div>
                    </Section>

                    {/* 3. How We Use Your Information */}
                    <Section number="3" title="How We Use Your Information" icon={<FiSettings />}>
                        <ul className="grid gap-3">
                            {[
                                "Provide, operate, and maintain our games and services.",
                                "Improve gameplay experience, performance, and stability.",
                                "Respond to your requests and provide customer support.",
                                "Understand usage patterns and fix issues through aggregated analytics.",
                                "Comply with legal obligations and protect the integrity and safety of our services."
                            ].map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-yellow-900/20 border border-yellow-500/10">
                                    <span className="text-yellow-400 mt-1">▸</span>
                                    <span className="text-black">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>

                    {/* 4. Sharing of Information */}
                    <Section number="4" title="Sharing of Information" icon={<FiUsers />}>
                        <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-400/20 mb-4">
                            <div className="flex items-center gap-3">
                                <FiAlertCircle className="text-black text-xl flex-shrink-0" />
                                <p className="font-bold text-black">
                                    We do not sell your personal information.
                                </p>
                            </div>
                        </div>
                        <p className="text-black mb-2">
                            We only share your information in very limited circumstances:
                        </p>
                        <ul className="list-disc list-inside ml-2 text-black">
                            <li><span className="font-semibold">Legal Requirements:</span> When required to do so by law, court order, or authorized government authorities.</li>
                        </ul>
                    </Section>

                    {/* 5. Data Security & Retention */}
                    <Section number="5" title="Data Security & Retention" icon={<FiLock />}>
                        <div className="space-y-4">
                            <div className="flex gap-3">
                                <span className="text-yellow-500 mt-1">●</span>
                                <p className="text-black"><span className="font-bold">Security Measures:</span> We implement reasonable technical and organizational measures to help safeguard your information. However, no method of transmission or storage is completely secure.</p>
                            </div>
                            <div className="flex gap-3">
                                <span className="text-yellow-500 mt-1">●</span>
                                <p className="text-black"><span className="font-bold">Data Retention:</span> We keep your data for as long as your account remains active or as necessary to fulfill the purposes outlined in this policy.</p>
                            </div>
                            <div className="flex gap-3">
                                <span className="text-yellow-500 mt-1">●</span>
                                <p className="text-black"><span className="font-bold">Deletion:</span> Upon your request or account deletion, your data will be deleted or anonymized where feasible.</p>
                            </div>
                            <div className="flex gap-3">
                                <span className="text-yellow-500 mt-1">●</span>
                                <p className="text-black"><span className="font-bold">Backup Storage:</span> Some user data may be stored securely on our servers for up to 6 months for backup and legal compliance purposes before final removal.</p>
                            </div>
                        </div>
                    </Section>

                    {/* 6. Your Rights */}
                    <Section number="6" title="Your Rights" icon={<FiUsers />}>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
                                <h4 className="font-semibold text-black mb-2">Access & Correction</h4>
                                <p className="text-sm text-black">
                                    You may request access to, correction of, or deletion of your personal data.
                                </p>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
                                <h4 className="font-semibold text-black mb-2">Account Deletion</h4>
                                <p className="text-sm text-black">
                                    If available, you can delete your account through in-game settings or by contacting us.
                                </p>
                            </div>
                        </div>
                    </Section>

                    {/* 7. Policy Updates */}
                    <Section number="7" title="Policy Updates" icon={<FiRefreshCw />}>
                        <p className="text-black">
                            We may update this policy periodically to reflect changes in our practices or services. Continued use of our services after an update means you accept the revised policy.
                        </p>
                    </Section>

                    {/* 8. Contact Us */}
                    <Section number="8" title="Contact Us" icon={<FiMail />}>
                        <p className="mb-2 text-black">If you have any questions about this policy or your data, contact Semperor at:</p>
                        <div className="inline-flex items-center gap-2 text-yellow-800 font-bold bg-yellow-400/20 px-4 py-2 rounded-lg mt-2">
                            <FiMail /> contact@semperorgames.com
                        </div>
                    </Section>

                </div>
            </div>
        </div>
    );
}

// Reusable Components

function Section({ number, title, icon, children }) {
    return (
        <div className="bg-yellow-600/90 backdrop-blur-md rounded-2xl p-6 sm:p-8 shadow-xl border border-yellow-500/20">
            <div className="flex items-center gap-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center border border-yellow-400/20 text-black text-xl">
                    {icon}
                </div>
                <h2 className="text-2xl font-bold text-black flex items-center gap-3">
                    <span className="opacity-30 text-lg font-normal text-yellow-300">#{number}</span> {title}
                </h2>
            </div>
            <div className="leading-relaxed">
                {children}
            </div>
        </div>
    );
}

function SubSection({ title, children }) {
    return (
        <div className="bg-black/10 border border-white/5 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-3 text-black flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                {title}
            </h3>
            <div className="pl-0 sm:pl-4">
                {children}
            </div>
        </div>
    );
}

function ListItem({ title, children }) {
    return (
        <li className="text-sm sm:text-base text-black flex gap-2">
            <span className="font-bold whitespace-nowrap text-yellow-900">{title}:</span>
            <span>{children}</span>
        </li>
    );
}

export default SemperorPrivacy;